import { isBefore, isToday, startOfToday } from 'date-fns';
import type { CrmTaskDTO } from '../server/dto/crm-task-dto';

/**
 * Determines if a task is overdue based on its due date
 * A task is considered overdue if:
 * 1. It has a due date
 * 2. The due date is in the past (before the start of the current day)
 *
 * @param task The task
 * @returns boolean indicating if the task is overdue
 */
export function isTaskOverdue(task: Pick<CrmTaskDTO, 'dueDate'>): boolean {
    // Task is overdue if the due date is before the start of today
    if (!task.dueDate) {
        return false;
    }
    return isBefore(new Date(task.dueDate), startOfToday());
}

/**
 * Determines if a task is due today
 *
 * @param task The task
 * @returns boolean
 */
export function isTaskDueToday(task: Pick<CrmTaskDTO, 'dueDate'>): boolean {
    if (!task.dueDate) {
        return false;
    }
    return isToday(new Date(task.dueDate));
}

/**
 * Determines if a task is due today or overdue
 *
 * @param task The task
 * @returns boolean
 */
export function isTaskUrgent(task: Pick<CrmTaskDTO, 'dueDate'>): boolean {
    return isTaskDueToday(task) || isTaskOverdue(task);
}

export function getDueDateColor(task: Pick<CrmTaskDTO, 'dueDate'>): 'red-600' | 'orange-600' | undefined {
    if (isTaskOverdue(task)) {
        return 'red-600';
    }
    if (isTaskDueToday(task)) {
        return 'orange-600';
    }
    return undefined;
}
